/**
 * Mapbox `Autocomplete` adapter — wraps the Search Box API (suggest +
 * retrieve, session-token grouped for billing) the LocationPickers already
 * use inline. Render-prop API only: the visible input chrome stays under the
 * consumer's control (Fluent UI on admin, Tailwind on website). This adapter
 * owns query state, debouncing, suggestion fetching, and the retrieve call
 * — the consumer renders an input + suggestion list against the api object.
 *
 * No-op when no token is configured.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type * as React from 'react';
import type {
  AutocompleteProps,
  AutocompleteRenderApi,
  AutocompleteSuggestion,
  AutocompleteSelection,
} from '../../types';
import { getMapboxToken } from './token';

interface MapboxSuggestion {
  mapbox_id: string;
  name: string;
  place_formatted?: string;
  full_address?: string;
  feature_type?: string;
}

const SUGGEST_BASE = 'https://api.mapbox.com/search/searchbox/v1/suggest';
const RETRIEVE_BASE = 'https://api.mapbox.com/search/searchbox/v1/retrieve';

export const MapboxAutocomplete: React.FC<AutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  country = 'pr',
  proximity = { lat: 18.2208, lng: -66.5901 },
  children,
}) => {
  const [internalQuery, setInternalQuery] = useState(value ?? '');
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const sessionTokenRef = useRef<string>('');
  const rawSuggestionsRef = useRef<Map<string, MapboxSuggestion>>(new Map());
  const onSelectRef = useRef(onSelect);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

  if (!sessionTokenRef.current && typeof window !== 'undefined') {
    sessionTokenRef.current =
      // crypto.randomUUID is available in all modern browsers + node 19+; the
      // fallback covers exotic JSDOM / older test envs.
      (typeof crypto !== 'undefined' && (crypto as Crypto).randomUUID?.()) ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  // Controlled / uncontrolled bridge — when the caller passes `value`, we
  // reflect it; the internal `setQuery` always emits via `onChange` so the
  // caller can keep its own state in sync.
  const query = value !== undefined ? value : internalQuery;
  const setQuery = useCallback(
    (q: string) => {
      if (value === undefined) setInternalQuery(q);
      onChange?.(q);
    },
    [value, onChange],
  );

  // Debounced suggest call. 300ms matches the historical inline picker.
  useEffect(() => {
    const q = query.trim();
    if (!q || q.length < 2) {
      setSuggestions([]);
      rawSuggestionsRef.current.clear();
      return;
    }
    const token = getMapboxToken();
    if (!token) return;

    const timer = window.setTimeout(async () => {
      setIsLoading(true);
      try {
        const url =
          `${SUGGEST_BASE}` +
          `?q=${encodeURIComponent(q)}` +
          `&access_token=${token}` +
          `&session_token=${sessionTokenRef.current}` +
          `&language=en` +
          `&limit=5` +
          `&country=${country}` +
          `&proximity=${proximity.lng},${proximity.lat}` +
          `&types=address,poi,street,place,neighborhood,postcode`;
        const res = await fetch(url);
        const data = await res.json();
        const raw: MapboxSuggestion[] = data.suggestions ?? [];
        rawSuggestionsRef.current.clear();
        const mapped: AutocompleteSuggestion[] = raw.map((s) => {
          rawSuggestionsRef.current.set(s.mapbox_id, s);
          return {
            id: s.mapbox_id,
            name: s.name,
            description: s.place_formatted ?? s.full_address,
          };
        });
        setSuggestions(mapped);
      } catch (err) {
        console.error('[maps:mapbox] suggest failed:', err);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(timer);
  }, [query, country, proximity.lat, proximity.lng]);

  const selectSuggestion = useCallback(async (s: AutocompleteSuggestion) => {
    const token = getMapboxToken();
    if (!token) return;
    const raw = rawSuggestionsRef.current.get(s.id);
    setIsLoading(true);
    try {
      const url =
        `${RETRIEVE_BASE}/${s.id}` +
        `?access_token=${token}` +
        `&session_token=${sessionTokenRef.current}`;
      const res = await fetch(url);
      const data = await res.json();
      const feature = data?.features?.[0];
      if (!feature) return;
      const [lng, lat] = feature.geometry.coordinates as [number, number];
      const props = feature.properties ?? {};
      const featureType: string | undefined = props.feature_type ?? raw?.feature_type;
      const name: string | undefined = props.name ?? raw?.name;
      const placeFormatted: string | undefined = props.place_formatted ?? raw?.place_formatted;
      const fullAddress: string | undefined = props.full_address ?? raw?.full_address;

      let address: string;
      let placeName: string | undefined;
      if (featureType === 'poi' && name && placeFormatted) {
        placeName = name;
        address = placeFormatted;
      } else {
        address = fullAddress ?? placeFormatted ?? name ?? '';
      }

      const selection: AutocompleteSelection = {
        lat,
        lng,
        address,
        placeName,
        placeId: s.id,
      };
      setSuggestions([]);
      rawSuggestionsRef.current.clear();
      onSelectRef.current(selection);
    } catch (err) {
      console.error('[maps:mapbox] retrieve failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const api: AutocompleteRenderApi = { query, setQuery, suggestions, isLoading, selectSuggestion };
  return <>{children ? children(api) : null}</>;
};
